import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { payment_links as paymentLinks } from '@repo/domain-types/cards-acceptance';
import ApplePayIcon from '#assets/small-icons/apple_pay.svg';
import CreditCardIcon from '#assets/small-icons/credit_card.svg';
import IdealIcon from '#assets/small-icons/ideal.svg';
import PaypalIcon from '#assets/small-icons/paypal.svg';
import { getPaymentMethodDisplayName } from '#utils/payment-method-names';
import styles from './avatar.module.css';

type PaymentMethodV3 = paymentLinks.firstparty.v3.ApiPaymentMethod;
type PaymentMethodV4 = paymentLinks.firstparty.v4.ApiPaymentMethod;
type PaymentMethod = PaymentMethodV3 | PaymentMethodV4;

const ApiPaymentMethodV3 = paymentLinks.firstparty.v3.ApiPaymentMethod;
const ApiPaymentMethodV4 = paymentLinks.firstparty.v4.ApiPaymentMethod;

interface PaymentMethodAvatarProps {
  paymentMethod: PaymentMethod;
  className?: string;
}

function getPaymentMethodAvatar(paymentMethod: PaymentMethod): string | null {
  switch (paymentMethod) {
    case ApiPaymentMethodV3.apple_pay:
    case ApiPaymentMethodV4.PAYMENT_METHOD_APPLE_PAY:
      return ApplePayIcon;
    case ApiPaymentMethodV3.credit_card:
    case ApiPaymentMethodV4.PAYMENT_METHOD_CREDIT_CARD:
      return CreditCardIcon;
    case ApiPaymentMethodV3.ideal:
    case ApiPaymentMethodV4.PAYMENT_METHOD_IDEAL:
      return IdealIcon;
    case ApiPaymentMethodV3.paypal:
    case ApiPaymentMethodV4.PAYMENT_METHOD_PAYPAL:
      return PaypalIcon;
    default:
      return null;
  }
}

function getPaymentMethodId(paymentMethod: PaymentMethod): string {
  switch (paymentMethod) {
    case ApiPaymentMethodV3.apple_pay:
    case ApiPaymentMethodV4.PAYMENT_METHOD_APPLE_PAY:
      return 'apple_pay';
    case ApiPaymentMethodV3.credit_card:
    case ApiPaymentMethodV4.PAYMENT_METHOD_CREDIT_CARD:
      return 'credit_card';
    case ApiPaymentMethodV3.ideal:
    case ApiPaymentMethodV4.PAYMENT_METHOD_IDEAL:
      return 'ideal';
    case ApiPaymentMethodV3.paypal:
    case ApiPaymentMethodV4.PAYMENT_METHOD_PAYPAL:
      return 'paypal';
    default:
      return '';
  }
}

export function PaymentMethodAvatar({ paymentMethod }: PaymentMethodAvatarProps): ReactNode {
  const { formatMessage } = useIntl();
  const icon = getPaymentMethodAvatar(paymentMethod);
  const paymentMethodId = getPaymentMethodId(paymentMethod);
  const altText = getPaymentMethodDisplayName(paymentMethodId, formatMessage);

  if (!icon) {
    return null;
  }

  return (
    <div className={styles.avatar}>
      <img alt={altText} src={icon} />
    </div>
  );
}
